/**
 * chatApi.js — Chat widget API client
 *
 * Wraps the E4 API gateway endpoints:
 *   POST /api/chat/session — create session
 *   GET  /api/chat/session?token= — resume session
 *   POST /api/chat/message — send message
 *
 * Related: agent-ops #52 (E5)
 */

const BASE = '/api/chat';

/**
 * Create a new anonymous chat session.
 * @param {object} params - { utm_source, utm_medium, utm_campaign, referrer, page_url }
 * @returns {{ ok: boolean, session_token: string, session: object } | { ok: false, error: string }}
 */
export async function createChatSession(params = {}) {
  try {
    const res = await fetch(`${BASE}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'Failed to create session' };
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

/**
 * Resume an existing chat session.
 * @param {string} token
 * @returns {{ ok: boolean, session: object } | { ok: false, error: string }}
 */
export async function resumeChatSession(token) {
  try {
    const res = await fetch(`${BASE}/session?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'Session not found' };
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

/**
 * Send a visitor message and get Vito's response.
 * @param {string} sessionToken
 * @param {string} message
 * @returns {{ ok: boolean, response: object, session: object } | { ok: false, error: string }}
 */
export async function sendChatMessage(sessionToken, message) {
  try {
    const res = await fetch(`${BASE}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': sessionToken,
      },
      body: JSON.stringify({ session_token: sessionToken, message }),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.error || 'Failed to send message' };
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}
